/* eslint-disable curly */
import { t } from "@excalidraw/excalidraw/i18n";
import { useEffect, useRef, useState } from "react";

import { libraryApi, type CanvasMeta } from "../data/libraryApi";

import "./CanvasTitleBar.scss";

interface Props {
  activeCanvasId: string;
  libraryVersion: number;
  onRenamed: () => void;
  theme: "light" | "dark";
}

export const CanvasTitleBar = ({
  activeCanvasId,
  libraryVersion,
  onRenamed,
  theme,
}: Props) => {
  const [canvas, setCanvas] = useState<CanvasMeta | null>(null);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    libraryApi
      .getCanvas(activeCanvasId)
      .then((c) => {
        if (!cancelled) setCanvas(c);
      })
      .catch(() => {
        if (!cancelled) setCanvas(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeCanvasId, libraryVersion]);

  useEffect(() => {
    if (editing) {
      const t = setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
      return () => clearTimeout(t);
    }
  }, [editing]);

  if (!canvas) return null;

  const commit = async () => {
    const v = draftName.trim();
    setEditing(false);
    if (!v || v === canvas.name) return;
    try {
      const updated = await libraryApi.updateCanvas(canvas.id, { name: v });
      setCanvas(updated);
      onRenamed();
    } catch {
      // restore previous on failure
    }
  };

  return (
    <div
      className={`canvas-title-bar${
        theme === "dark" ? " canvas-title-bar--dark" : ""
      }`}
    >
      {editing ? (
        <input
          ref={inputRef}
          className="canvas-title-bar__input"
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              setEditing(false);
            }
          }}
          maxLength={120}
        />
      ) : (
        <button
          type="button"
          className="canvas-title-bar__name"
          onClick={() => {
            setDraftName(canvas.name);
            setEditing(true);
          }}
          title={t("canvasLibrary.clickToRename")}
        >
          {canvas.name}
        </button>
      )}
    </div>
  );
};
